import React, { useEffect, useState } from "react";
import { UserCircle, Plus, Trash2, Save, Download, Sparkles, X, Camera } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const EMPTY_EXP = { title: "", company: "", location: "", start: "", end: "", current: false, description: "" };
const EMPTY_EDU = { degree: "", institution: "", start: "", end: "", description: "" };
const EMPTY_CERT = { name: "", issuer: "", year: "" };
const EMPTY_LANG = { name: "", level: "" };

/** CV Profile editor + AI-tailored PDF generator. */
export default function CvProfileSection() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setList = (key, list) => setForm((f) => ({ ...f, [key]: list }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/cv-profile");
        setForm({
          headline: data.headline || "",
          summary: data.summary || "",
          photo_base64: data.photo_base64 || "",
          portfolio_url: data.portfolio_url || "",
          linkedin_url: data.linkedin_url || "",
          xing_url: data.xing_url || "",
          instagram_url: data.instagram_url || "",
          website_url: data.website_url || "",
          phone: data.phone || "",
          location: data.location || "",
          desired_jobs: data.desired_jobs || [],
          skills: data.skills || [],
          work_experience: data.work_experience || [],
          education: data.education || [],
          certifications: data.certifications || [],
          languages: data.languages || [],
        });
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/cv-profile", form);
      toast.success("CV-Profil gespeichert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Bitte ein Foto unter 500 KB wählen.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set({ photo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  if (loading || !form) return <div className="text-sm text-slate-500 py-6 text-center">Lädt CV-Profil…</div>;

  return (
    <div className="border border-slate-200 bg-white p-6" data-testid="cv-profile-section">
      <div className="flex items-baseline justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
            <UserCircle size={11} strokeWidth={1.5} /> Bewerbungsprofil
          </div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Mein CV-Profil</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setGenerateOpen(true)}
            className="px-3 py-1.5 text-xs font-medium border border-slate-900 bg-white text-slate-900 hover:bg-slate-900 hover:text-white flex items-center gap-1"
            data-testid="open-cv-generate">
            <Sparkles size={12} strokeWidth={1.5} /> CV als PDF
          </button>
          <button onClick={() => setCoverOpen(true)}
            className="px-3 py-1.5 text-xs font-medium border border-slate-900 bg-white text-slate-900 hover:bg-slate-900 hover:text-white flex items-center gap-1"
            data-testid="open-cover-letter">
            <Sparkles size={12} strokeWidth={1.5} /> Anschreiben
          </button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1 disabled:opacity-50"
            data-testid="save-cv-btn">
            <Save size={12} strokeWidth={1.5} /> {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>

      {/* Photo + Basics */}
      <div className="grid grid-cols-1 md:grid-cols-[130px_1fr] gap-4 mb-4">
        <div>
          <div className="w-28 h-36 bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
            {form.photo_base64 ? (
              <img src={form.photo_base64} alt="Portrait" className="w-full h-full object-cover" />
            ) : (
              <Camera size={22} strokeWidth={1.2} className="text-slate-400" />
            )}
          </div>
          <label className="mt-2 inline-block px-2 py-1 text-[10px] border border-slate-300 hover:bg-slate-100 cursor-pointer" data-testid="upload-photo">
            <input type="file" accept="image/*" onChange={onPhotoChange} className="hidden" />
            Foto hochladen
          </label>
          {form.photo_base64 && (
            <button onClick={() => set({ photo_base64: "" })} className="ml-1 text-[10px] text-red-600 hover:underline">
              entfernen
            </button>
          )}
        </div>
        <div className="space-y-3">
          <Field label="Berufsbezeichnung">
            <input value={form.headline} onChange={(e) => set({ headline: e.target.value })}
              placeholder="z. B. UX-Designerin · Grafikdesigner/-in"
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              data-testid="cv-headline" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefon">
              <input value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900" />
            </Field>
            <Field label="Wohnort">
              <input value={form.location} onChange={(e) => set({ location: e.target.value })}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900" />
            </Field>
          </div>
          <Field label="Zusammenfassung (3-5 Sätze)">
            <textarea value={form.summary} onChange={(e) => set({ summary: e.target.value })}
              rows={3}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="Kurzer Steckbrief deiner beruflichen Identität, Stärken und Ausrichtung."
              data-testid="cv-summary" />
          </Field>
        </div>
      </div>

      {/* Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <Field label="Portfolio-Link"><LinkInput value={form.portfolio_url} onChange={(v) => set({ portfolio_url: v })} placeholder="https://…" testid="cv-portfolio" /></Field>
        <Field label="LinkedIn"><LinkInput value={form.linkedin_url} onChange={(v) => set({ linkedin_url: v })} placeholder="https://linkedin.com/in/…" /></Field>
        <Field label="Xing"><LinkInput value={form.xing_url} onChange={(v) => set({ xing_url: v })} placeholder="https://xing.com/profile/…" /></Field>
        <Field label="Instagram"><LinkInput value={form.instagram_url} onChange={(v) => set({ instagram_url: v })} placeholder="https://instagram.com/…" /></Field>
        <Field label="Website"><LinkInput value={form.website_url} onChange={(v) => set({ website_url: v })} placeholder="https://…" /></Field>
      </div>

      {/* Target roles */}
      <TagsField label="Gesuchte Positionen / Berufe" values={form.desired_jobs} onChange={(v) => set({ desired_jobs: v })} placeholder="z. B. UX-Designerin, Motion-Designer/-in" testid="cv-desired" />

      {/* Skills */}
      <TagsField label="Fähigkeiten / Qualifikationen" values={form.skills} onChange={(v) => set({ skills: v })} placeholder="z. B. Figma, Adobe CC, Storytelling, HTML/CSS" testid="cv-skills" />

      {/* Work Experience */}
      <ListSection label="Berufserfahrung"
        items={form.work_experience} onChange={(v) => setList("work_experience", v)}
        empty={EMPTY_EXP}
        renderItem={(it, upd) => (
          <>
            <div className="grid grid-cols-2 gap-2">
              <input value={it.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Position" className="input-tech text-sm" />
              <input value={it.company} onChange={(e) => upd({ company: e.target.value })} placeholder="Unternehmen" className="input-tech text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <input value={it.start} onChange={(e) => upd({ start: e.target.value })} placeholder="Start (YYYY-MM)" className="input-tech text-xs font-mono" />
              <input value={it.end} onChange={(e) => upd({ end: e.target.value })} placeholder="Ende (YYYY-MM)" className="input-tech text-xs font-mono" disabled={it.current} />
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={!!it.current} onChange={(e) => upd({ current: e.target.checked })} /> laufend</label>
            </div>
            <textarea value={it.description} onChange={(e) => upd({ description: e.target.value })} rows={2}
              placeholder="Kurze Beschreibung deiner Aufgaben & Erfolge"
              className="input-tech text-sm mt-2 w-full" />
          </>
        )}
        testid="cv-exp" />

      {/* Education */}
      <ListSection label="Ausbildung / Weiterbildung"
        items={form.education} onChange={(v) => setList("education", v)}
        empty={EMPTY_EDU}
        renderItem={(it, upd) => (
          <>
            <div className="grid grid-cols-2 gap-2">
              <input value={it.degree} onChange={(e) => upd({ degree: e.target.value })} placeholder="Abschluss / Titel" className="input-tech text-sm" />
              <input value={it.institution} onChange={(e) => upd({ institution: e.target.value })} placeholder="Institution" className="input-tech text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input value={it.start} onChange={(e) => upd({ start: e.target.value })} placeholder="Von (YYYY-MM)" className="input-tech text-xs font-mono" />
              <input value={it.end} onChange={(e) => upd({ end: e.target.value })} placeholder="Bis (YYYY-MM)" className="input-tech text-xs font-mono" />
            </div>
          </>
        )}
        testid="cv-edu" />

      {/* Certifications */}
      <ListSection label="Zertifikate"
        items={form.certifications} onChange={(v) => setList("certifications", v)}
        empty={EMPTY_CERT}
        renderItem={(it, upd) => (
          <div className="grid grid-cols-3 gap-2">
            <input value={it.name} onChange={(e) => upd({ name: e.target.value })} placeholder="Zertifikat" className="input-tech text-sm" />
            <input value={it.issuer} onChange={(e) => upd({ issuer: e.target.value })} placeholder="Aussteller" className="input-tech text-sm" />
            <input value={it.year} onChange={(e) => upd({ year: e.target.value })} placeholder="Jahr" className="input-tech text-xs font-mono" />
          </div>
        )}
        testid="cv-cert" />

      {/* Languages */}
      <ListSection label="Sprachen"
        items={form.languages} onChange={(v) => setList("languages", v)}
        empty={EMPTY_LANG}
        renderItem={(it, upd) => (
          <div className="grid grid-cols-2 gap-2">
            <input value={it.name} onChange={(e) => upd({ name: e.target.value })} placeholder="Sprache" className="input-tech text-sm" />
            <input value={it.level} onChange={(e) => upd({ level: e.target.value })} placeholder="Level (B2, C1, Muttersprache…)" className="input-tech text-sm" />
          </div>
        )}
        testid="cv-lang" />

      {generateOpen && (
        <GenerateCvDialog onClose={() => setGenerateOpen(false)} />
      )}
      {coverOpen && (
        <CoverLetterDialog onClose={() => setCoverOpen(false)} />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

// Mini visual thumbnail (SVG) showing each CV template's layout
function CvThumbnail({ variant }) {
  const accent = { classic: "#0A0A0A", modern: "#00CCC9", creative: "#0A0A0A" }[variant];
  const showBand = variant !== "classic";
  const bandCol = variant === "modern" ? "#00CCC9" : "#0A0A0A";
  const photoR = variant === "creative" ? 6 : 0;
  return (
    <svg viewBox="0 0 100 130" className="w-full h-24 bg-white border border-slate-200">
      {/* band */}
      {showBand && <rect x="0" y="0" width="100" height="4" fill={bandCol} />}
      {/* name */}
      <rect x="6" y={showBand ? 10 : 6} width="42" height="4" fill={accent} rx="1" />
      <rect x="6" y={showBand ? 18 : 14} width="30" height="2" fill="#999" rx="1" />
      {/* photo */}
      <rect x="72" y={showBand ? 8 : 4} width="20" height="24" fill="#DDD" rx={photoR} />
      {/* divider */}
      <line x1="6" y1="38" x2="94" y2="38" stroke={accent} strokeWidth="0.5" />
      {/* section heads */}
      <rect x="6" y="44" width="20" height="2.5" fill={accent} rx="1" />
      <rect x="6" y="50" width="80" height="1.5" fill="#DDD" rx="1" />
      <rect x="6" y="54" width="70" height="1.5" fill="#DDD" rx="1" />
      <rect x="6" y="58" width="75" height="1.5" fill="#DDD" rx="1" />
      <rect x="6" y="68" width="24" height="2.5" fill={accent} rx="1" />
      <rect x="6" y="74" width="60" height="1.5" fill="#333" rx="1" />
      <rect x="6" y="78" width="85" height="1.5" fill="#BBB" rx="1" />
      <rect x="6" y="82" width="75" height="1.5" fill="#BBB" rx="1" />
      <rect x="6" y="92" width="16" height="2.5" fill={accent} rx="1" />
      <rect x="6" y="98" width="88" height="1.5" fill="#BBB" rx="1" />
      <rect x="6" y="102" width="70" height="1.5" fill="#BBB" rx="1" />
      <rect x="6" y="112" width="20" height="2.5" fill={accent} rx="1" />
      <rect x="6" y="118" width="85" height="1.5" fill="#BBB" rx="1" />
    </svg>
  );
}
function LinkInput({ value, onChange, placeholder, testid }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-slate-900" />;
}

function TagsField({ label, values, onChange, placeholder, testid }) {
  const [txt, setTxt] = useState("");
  const add = () => {
    const v = txt.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setTxt("");
  };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div className="mb-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex gap-2 mb-2">
        <input value={txt} onChange={(e) => setTxt(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
          data-testid={testid} />
        <button onClick={add} className="px-3 py-2 text-xs bg-slate-900 text-white hover:bg-black"><Plus size={12} strokeWidth={2} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-slate-100 border border-slate-200">
            {v}
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-600"><X size={10} strokeWidth={2} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function ListSection({ label, items, onChange, empty, renderItem, testid }) {
  const add = () => onChange([...items, { ...empty }]);
  const upd = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const del = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <button onClick={add} className="text-[11px] text-slate-700 hover:text-slate-900 underline flex items-center gap-1">
          <Plus size={11} strokeWidth={2} /> Eintrag
        </button>
      </div>
      {items.length === 0 && (
        <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 p-3 text-center">
          Noch kein Eintrag.
        </div>
      )}
      <div className="space-y-3">
        {items.map((it, idx) => (
          <div key={idx} className="border border-slate-200 p-3 relative">
            <button onClick={() => del(idx)}
              className="absolute top-2 right-2 text-slate-400 hover:text-red-600">
              <Trash2 size={12} strokeWidth={1.5} />
            </button>
            {renderItem(it, (p) => upd(idx, p))}
          </div>
        ))}
      </div>
    </div>
  );
}

function GenerateCvDialog({ onClose }) {
  const [jobPosting, setJobPosting] = useState("");
  const [language, setLanguage] = useState("de");
  const [template, setTemplate] = useState("classic");
  const [typography, setTypography] = useState("helvetica");
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/me/cv/generate`, { job_posting: jobPosting, language, template, typography }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `cv.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CV heruntergeladen");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">CV als PDF generieren</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Wähle Design und Sprache. Optional: Stellenanzeige einfügen — die KI passt Zusammenfassung + Skills an (ATS-freundlich).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Design</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "classic",  l: "Klassisch",  s: "Schlicht, S/W" },
                { k: "modern",   l: "Modern",     s: "Türkis-Akzent" },
                { k: "creative", l: "Kreativ",    s: "Farbband oben" },
              ].map((o) => (
                <button key={o.k} onClick={() => setTemplate(o.k)}
                  className={`p-2 text-left border transition-all ${template === o.k ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`cv-template-${o.k}`}>
                  <CvThumbnail variant={o.k} />
                  <div className="text-xs font-medium text-slate-900 mt-1.5">{o.l}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{o.s}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Typografie</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "helvetica", l: "Helvetica", s: "Klassisch, ATS-Standard" },
                { k: "times",     l: "Times",     s: "Serif, seriös" },
                { k: "courier",   l: "Courier",   s: "Modern-Tech-Look" },
              ].map((o) => (
                <button key={o.k} onClick={() => setTypography(o.k)}
                  className={`p-2 text-left border transition-all ${typography === o.k ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`cv-typo-${o.k}`}>
                  <div className={`text-[11px] font-medium text-slate-900 ${o.k === "times" ? "font-serif" : o.k === "courier" ? "font-mono" : ""}`}>{o.l}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{o.s}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Sprache</div>
            <div className="flex gap-2">
              {[{ k: "de", l: "Deutsch" }, { k: "en", l: "English" }].map((o) => (
                <button key={o.k} onClick={() => setLanguage(o.k)}
                  className={`px-3 py-1.5 text-xs border ${language === o.k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`cv-lang-${o.k}`}>{o.l}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Stellenanzeige (optional · für KI-Anpassung)
            </div>
            <textarea value={jobPosting} onChange={(e) => setJobPosting(e.target.value)}
              rows={6}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="Füge hier die Stellenanzeige ein. Lass leer, um einen neutralen CV zu generieren."
              data-testid="cv-job-posting" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Abbrechen</button>
          <button onClick={generate} disabled={generating}
            className="px-5 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2 disabled:opacity-50"
            data-testid="generate-cv-btn">
            <Download size={14} strokeWidth={1.5} /> {generating ? "Erstelle…" : "PDF herunterladen"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CoverLetterDialog({ onClose }) {
  const [jobPosting, setJobPosting] = useState("");
  const [language, setLanguage] = useState("de");
  const [hiringCompany, setHiringCompany] = useState("");
  const [hiringContact, setHiringContact] = useState("");
  const [typography, setTypography] = useState("helvetica");
  const [step, setStep] = useState("input");   // input | preview
  const [body, setBody] = useState("");
  const [refineHint, setRefineHint] = useState("");
  const [busy, setBusy] = useState(false);

  const draft = async (hint) => {
    if (!jobPosting.trim()) { toast.error("Bitte Stellenanzeige einfügen"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/me/cv/cover-letter/draft`, {
        job_posting: jobPosting,
        language,
        hiring_company: hiringCompany,
        hiring_contact: hiringContact,
        refine_hint: hint || "",
        body: hint ? body : "",
      });
      setBody(data.body || "");
      setStep("preview");
      if (hint) toast.success("KI hat den Text überarbeitet");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/me/cv/cover-letter`, {
        job_posting: jobPosting, language,
        hiring_company: hiringCompany, hiring_contact: hiringContact,
        body_override: body,
        typography,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `anschreiben.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Anschreiben heruntergeladen");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const applyRefine = async () => {
    if (!refineHint.trim()) { toast.error("Bitte Tipp für die KI eingeben"); return; }
    await draft(refineHint);
    setRefineHint("");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            Anschreiben {step === "preview" ? "prüfen &amp; anpassen" : "generieren"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {step === "input"
              ? "Fülle unten die Details aus. Danach zeigt dir die KI einen Entwurf, den du bearbeiten oder verbessern lassen kannst."
              : 'Prüfe den Text. Du kannst manuell editieren oder der KI einen Verbesserungstipp geben (kürzer, formeller, mehr Details zu XY).'}
          </DialogDescription>
        </DialogHeader>

        {step === "input" && (
          <div className="space-y-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Sprache</div>
              <div className="flex gap-2">
                {[{ k: "de", l: "Deutsch" }, { k: "en", l: "English" }].map((o) => (
                  <button key={o.k} onClick={() => setLanguage(o.k)}
                    className={`px-3 py-1.5 text-xs border ${language === o.k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid={`cover-lang-${o.k}`}>{o.l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Typografie</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { k: "helvetica", l: "Helvetica" },
                  { k: "times",     l: "Times" },
                  { k: "courier",   l: "Courier" },
                ].map((o) => (
                  <button key={o.k} onClick={() => setTypography(o.k)}
                    className={`p-2 border text-xs ${typography === o.k ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:bg-slate-50"} ${o.k === "times" ? "font-serif" : o.k === "courier" ? "font-mono" : ""}`}
                    data-testid={`cover-typo-${o.k}`}>{o.l}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Unternehmen (optional)</div>
                <input value={hiringCompany} onChange={(e) => setHiringCompany(e.target.value)}
                  placeholder="z. B. Studio Musterstadt"
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                  data-testid="cover-company" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Ansprechpartner (optional)</div>
                <input value={hiringContact} onChange={(e) => setHiringContact(e.target.value)}
                  placeholder="z. B. Frau Müller"
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                  data-testid="cover-contact" />
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Stellenanzeige *</div>
              <textarea value={jobPosting} onChange={(e) => setJobPosting(e.target.value)}
                rows={8}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                placeholder="Füge hier die Stellenanzeige ein. Die KI passt Text + Argumentation auf die Anzeige an."
                data-testid="cover-job-posting" />
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Entwurf des Anschreibens · frei editierbar
              </div>
              <textarea value={body} onChange={(e) => setBody(e.target.value)}
                rows={16}
                className={`w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900 ${typography === "times" ? "font-serif" : typography === "courier" ? "font-mono" : ""}`}
                data-testid="cover-body-editor" />
              <div className="text-[10px] text-slate-500 mt-1">
                {body.length} Zeichen · ca. {Math.max(1, Math.round(body.split(/\s+/).filter(Boolean).length))} Wörter
              </div>
            </div>
            <div className="border border-slate-200 p-3 bg-slate-50">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                KI-Verbesserung anfragen
              </div>
              <div className="flex gap-2 flex-wrap mb-2">
                {["kürzer machen", "formeller", "persönlicher", "mehr Zahlen & Fakten", "auf Englisch übersetzen"].map((h) => (
                  <button key={h} onClick={() => setRefineHint(h)}
                    className="text-[11px] px-2 py-1 border border-slate-300 bg-white hover:bg-slate-900 hover:text-white"
                    data-testid={`refine-preset-${h.slice(0,10)}`}>{h}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={refineHint} onChange={(e) => setRefineHint(e.target.value)}
                  placeholder='z. B. "stärkere Betonung meiner Figma-Erfahrung"'
                  className="flex-1 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                  data-testid="cover-refine-input" />
                <button onClick={applyRefine} disabled={busy}
                  className="px-3 py-2 bg-slate-900 text-white text-xs font-medium hover:bg-black flex items-center gap-1 disabled:opacity-50"
                  data-testid="cover-refine-btn">
                  <Sparkles size={12} strokeWidth={1.5} /> {busy ? "…" : "Verbessern"}
                </button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "input" && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Abbrechen</button>
              <button onClick={() => draft("")} disabled={busy}
                className="px-5 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2 disabled:opacity-50"
                data-testid="cover-draft-btn">
                <Sparkles size={14} strokeWidth={1.5} /> {busy ? "KI schreibt…" : "KI-Entwurf erstellen"}
              </button>
            </>
          )}
          {step === "preview" && (
            <>
              <button onClick={() => setStep("input")}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">← Zurück</button>
              <button onClick={download} disabled={busy || !body.trim()}
                className="px-5 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2 disabled:opacity-50"
                data-testid="generate-cover-btn">
                <Download size={14} strokeWidth={1.5} /> {busy ? "Erstelle…" : "PDF herunterladen"}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
